/** @jsxImportSource ./simboluri-jsx */
// Capul DRAWER-ului de element din Studio: identitatea o dată, verdictele cu
// drum spre verificări, și COSTUL cu rețeta lui de calcul (
// varianta B din planșa de design: drawer pe toată înălțimea, fiindcă
// informația nu mai încape într-un card plutitor). Proprietățile de calcul
// rămân dedesubt, în `PanouProprietati`.

import { useEffect, useMemo, useState } from "react";
import { useT, useTn } from "../i18n";
import type { Cheie } from "../i18n";
import { FemResult, StepState, eLamela } from "./tipuri";
import { bani } from "./bani";
import { CostElement, costElement } from "./costElement";
import {
  VERDICT_CHIP,
  gasesteElement,
  indexElemente,
  nr,
} from "./elemente";
import { hashElement, hashSectiune, pasScurt, refCuNivel } from "./legaturi";
import {
  SelectieFem,
  fundareaDesenata,
  goluriFlat,
  parapeteFlat,
  zidarieFlat,
} from "./StepFem";

/** Id-ul de plan al selecției din scenă („GR12.2", „ST7.1", „OP3.2"…).
 *  Scena ține INDICI; `id_plan` poartă deja sufixul de nivel, deci forma
 *  finală o decide `refCuNivel` (v. legaturi.ts). */
export function refDinSelectie(r: FemResult, sel: SelectieFem): string | null {
  if (!sel) return null;
  const m = r.model;
  if (sel.tip === "stalp") {
    const s = m.stalpi?.[sel.ref];
    return refCuNivel(s?.id_plan, s?.etaj);
  }
  if (sel.tip === "grinda") {
    const g = m.grinzi?.[sel.ref];
    return refCuNivel(g?.id_plan, g?.etaj);
  }
  if (sel.tip === "perete_beton") {
    const p = m.pereti_beton?.[sel.ref];
    return p?.id_plan ?? null;
  }
  if (sel.tip === "panou") {
    const p = m.panouri?.[sel.ref];
    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
    return (p as any)?.id ?? null;
  }
  // grinda de fundare: elementul navigabil e AXUL („Ax 4"), nu tronsonul
  // dintre două noduri — el are pagină, verificări și cost
  if (sel.tip === "fundatie") return fundareaDesenata(m).grinzi[sel.ref]?.ax ?? null;
  // placa pe sol: componenta ei de amprentă («PS1»), ca în indexul de
  // elemente (v. `elemente.indexElemente`) — fără ramura asta drawer-ul ieșea
  // gol, fără cap și fără drum spre pagină
  if (sel.tip === "placa_pe_sol") {
    const ps = m.placa_pe_sol;
    return ps ? String(ps.elemente?.[sel.ref]?.element ?? "PS1") : null;
  }
  if (sel.tip === "zidarie_confinata") return m.pereti_zidarie?.[sel.ref]?.id_plan ?? null;
  // piesele de ARHITECTURĂ n-au pagină de element, dar au id: fără el
  // drawer-ul Studioului rămânea complet fără cap (titlul din
  // `PanouProprietati` e ascuns prin CSS aici)
  if (sel.tip === "perete_zidarie") return zidarieFlat(m)[sel.ref]?.id_plan ?? null;
  if (sel.tip === "fereastra" || sel.tip === "usa")
    return goluriFlat(m, sel.tip)[sel.ref]?.id_plan ?? null;
  if (sel.tip === "parapet") return parapeteFlat(m)[sel.ref]?.id_plan ?? null;
  return null;
}

/** CE FEL de element e selecția — cuvântul din capul panoului.
 *
 *  În Studio titlul lui `PanouProprietati` e ascuns prin CSS (identitatea se
 *  scrie o singură dată, aici), deci fără cuvântul ăsta drawer-ul arăta doar
 *  id-ul: «vreau ca UI-ul din dreapta să arate și ce fel de element e, nu
 * doar id-ul». */
export function felSelectie(sel: SelectieFem, r?: FemResult): Cheie | null {
  switch (sel?.tip) {
    case "stalp":
      // o lamelă e tot un element din familia stâlpilor, cu id-ul lor, dar
      // pasul care o armează e al pereților: cuvântul din cap o spune
      return r?.model?.stalpi?.[sel.ref] && eLamela(r.model.stalpi[sel.ref])
        ? "struct.fel.lamela" : "struct.fel.stalp";
    case "grinda":
      return "struct.fel.grinda";
    case "perete_beton":
      return "struct.fel.perete_beton";
    case "perete_zidarie":
      return "struct.fel.perete_zidarie";
    case "panou":
      return "struct.fel.panou";
    case "fundatie":
      return "struct.fel.fundatie";
    case "placa_pe_sol":
      return "struct.fel.placa_sol";
    case "zidarie_confinata":
      return "struct.fel.zidarie_confinata";
    case "fereastra":
      return "struct.fel.fereastra";
    case "usa":
      return "struct.fel.usa";
    case "parapet":
      return "struct.fel.parapet";
    default:
      return null;
  }
}

/** Pașii din care vine elementul selecției — verdictul, verificările,
 *  cantitățile pentru cost. Planul Studioului nu aduce armările (megaocteți,
 *  v. `StructureContext.ordineaIncarcarii`), deci drawer-ul le cere pe ale
 *  piesei alese, și numai pe ele: fără asta indexul rămânea gol, iar orice
 *  piesă ieșea «nu e în rezultatele pașilor de armare», fără cost și fără
 *  «sari la verificări».
 *
 *  Lista goală = piesa n-are pagină de element (arhitectura, zidăria
 *  confinată — niciun pas nu le armează). */
export function pasiSelectie(sel: SelectieFem, r?: FemResult): string[] {
  switch (sel?.tip) {
    case "stalp":
      // lamela se armează la Pasul 14, cu pereții
      return r?.model?.stalpi?.[sel.ref] && eLamela(r.model.stalpi[sel.ref])
        ? ["armare_pereti"] : ["armare_stalpi"];
    case "grinda":
      return ["armare_grinzi"];
    case "perete_beton":
      return ["armare_pereti"];
    case "panou":
      return ["armare_placa"];
    // axul de fundare se compune din Pașii 17 și 18 (`fundatiaAdoptata`)
    case "fundatie":
      return ["armare_fundatie", "verificare_fundatie", "rezistenta_fundatie"];
    case "placa_pe_sol":
      return ["armare_fundatie"];
    default:
      return [];
  }
}

export default function StudioSumar({
  id,
  cloneId,
  solutie,
  r,
  sel,
  steps,
  seIncarca = false,
  onClose,
}: {
  id: string;
  cloneId: string | null;
  solutie?: string | null;
  r: FemResult;
  sel: SelectieFem;
  steps: Record<string, StepState>;
  /** pașii piesei (`pasiSelectie`) sunt pe drum: verdictul și costul vin */
  seIncarca?: boolean;
  onClose: () => void;
}) {
  const ref = refDinSelectie(r, sel);
  const t = useT();
  const tn = useTn();
  const fel = felSelectie(sel, r);
  const arePagina = pasiSelectie(sel, r).length > 0;
  // indexul se reconstruiește din rezultatele pașilor: fără memoizare, `el` e
  // un obiect NOU la fiecare render, iar efectul de cost de mai jos s-ar
  // reporni la infinit (panoul rămânea pe «se calculează…»)
  const idx = useMemo(() => indexElemente(steps), [steps]);
  const el = useMemo(() => (ref ? gasesteElement(idx, ref) : null), [idx, ref]);

  const [cost, setCost] = useState<CostElement | null>(null);
  const [costGata, setCostGata] = useState(false);
  useEffect(() => {
    if (!el) {
      setCost(null);
      // cât vin pașii piesei, costul e «se calculează», nu «devizul lipsește»
      setCostGata(!seIncarca);
      return;
    }
    let viu = true;
    setCost(null);
    setCostGata(false);
    costElement(id, el, cloneId)
      .then((c) => viu && setCost(c))
      .catch(() => undefined)
      .finally(() => viu && setCostGata(true));
    return () => {
      viu = false;
    };
    // dependența e REFERINȚA elementului (string), nu obiectul lui
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, cloneId, el?.ref, el?.tip, seIncarca]);

  if (!ref) return null;

  const nrPe = (fel: "conform" | "la_limita" | "neconform") =>
    (el?.verificari ?? []).filter((v) => {
      const n = v.verdict.startsWith("SE VERIFICA")
        ? "conform"
        : v.verdict.startsWith("ATENTIE")
          ? "la_limita"
          : "neconform";
      return n === fel;
    }).length;

  const picate = nrPe("neconform");
  const limita = nrPe("la_limita");
  const trec = nrPe("conform");
  const chip = el ? VERDICT_CHIP[el.verdict] : null;

  return (
    <>
      {/* capul drawer-ului: identitatea, o SINGURĂ dată (titlul dublat din
          PanouProprietati e ascuns prin CSS în `.ob-studio-inspector`) */}
      <div className="ob-studio-sumar">
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <b className="ob-disp" style={{ fontSize: 17 }}>
            {fel ? `${t(fel)} ` : ""}
            {ref}
          </b>
          {el && (
            <span className="ob-chip ob-c-mut" style={{ fontSize: 10 }}>
              {el.nivel}
              {el.sectiune !== "—" ? ` · ${el.sectiune}` : ""}
            </span>
          )}
          <span style={{ flex: 1 }} />
          {chip && <span className={`ob-chip ${chip.cls}`} style={{ fontSize: 10 }}>{t(chip.text)}</span>}
          <button
            className="ob-studio-inchide"
            onClick={onClose}
            title={t("struct.sumar.inchide")}
            aria-label={t("struct.drax.inchide")}
          >
            ×
          </button>
        </div>

        {el?.reMin != null && (
          <div style={{ display: "flex", alignItems: "center", gap: 9, marginTop: 7 }}>
            <span className="ob-lbl">{t("struct.elp.re_minim")}</span>
            <span style={{ width: 104, height: 5, borderRadius: 99, background: "#e5e5e5", position: "relative", display: "inline-block" }}>
              <span style={{
                position: "absolute", left: 0, top: 0, height: "100%",
                width: `${Math.max(4, Math.min(100, (el.reMin / 1.35) * 74))}%`,
                borderRadius: 99,
                background: el.reMin < 1 ? "var(--bad)" : el.reMin < 1.1 ? "var(--amb)" : "var(--ok)",
              }} />
              <span style={{ position: "absolute", left: "74%", top: -3, width: 1.5, height: 11, background: "var(--ink)" }} />
            </span>
            <b className="sg-mono" style={{ fontSize: 12 }}>{nr(el.reMin, 2)}</b>
            <span style={{ fontSize: 11, color: "var(--mut)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {el.criteriu}
            </span>
          </div>
        )}

        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginTop: 7 }}>
          {el ? (
            <>
              {picate > 0 && (
                <span className="ob-chip ob-c-bad" style={{ fontSize: 10 }}>
                  {tn("struct.sumar.picate#n", picate)}
                </span>
              )}
              {limita > 0 && (
                <span className="ob-chip ob-c-amb" style={{ fontSize: 10 }}>
                  {t("struct.sumar.la_limita", { n: limita })}
                </span>
              )}
              <span className="ob-chip ob-c-ok" style={{ fontSize: 10 }}>
                {t("struct.sumar.trec", { n: trec })}
              </span>
              <a className="ob-sari" href={hashElement(id, ref, solutie)}
                 title={t("struct.sumar.sari_titlu", { pas: pasScurt(el.pas) })}>
                {t("struct.sumar.sari")}
              </a>
            </>
          ) : (
            <span className="sg-muted" style={{ fontSize: 11 }}>
              {t(seIncarca ? "struct.sumar.se_incarca" : "struct.sumar.nu_e")}
            </span>
          )}
        </div>
      </div>

      {/* COSTUL cu rețeta de calcul — al pieselor structurii; o fereastră
          n-are deviz de structură, iar «devizul nu s-a putut citi» o mințea */}
      {arePagina && <div className="ob-studio-cost">
        <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
          <span className="ob-lbl">{t("struct.sumar.cost")}</span>
          <span style={{ flex: 1 }} />
          {cost && (
            <b className="ob-disp" style={{ fontSize: 17 }}>{bani(cost.totalEur)}</b>
          )}
        </div>
        {!costGata ? (
          <div className="sg-muted" style={{ fontSize: 11, marginTop: 4 }}>{t("struct.sumar.se_calculeaza")}</div>
        ) : !cost ? (
          <div className="sg-muted" style={{ fontSize: 11, marginTop: 4 }}>
            {t("struct.sumar.fara_deviz")}
          </div>
        ) : (
          <>
            <div style={{ marginTop: 5 }}>
              {cost.randuri.map((x, i) => (
                <div key={i} className="ob-cost-rand">
                  <span className="sg-mono ob-cost-q">
                    {x.cantitate}
                    {x.derivat && (
                      <span className="ob-cost-derivat" title={t("struct.sumar.derivat_titlu")}>
                        ~
                      </span>
                    )}
                  </span>
                  <span className="ob-cost-d">{x.reteta}</span>
                  <span className="sg-mono ob-cost-s">{bani(x.eur)}</span>
                </div>
              ))}
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", paddingTop: 7, fontSize: 12 }}>
              <span style={{ color: "var(--mut)" }}>
                {t("struct.sumar.material_manopera", {
                  material: bani(cost.materialEur),
                  manopera: bani(cost.manoperaEur),
                })}
              </span>
              <a className="ob-sari" href={hashSectiune(id, "retete", solutie ?? null)}
                 title={t("struct.sumar.deviz_titlu", { sursa: cost.sursa.join(" · ") })}>
                {t("struct.sumar.deviz")}
              </a>
            </div>
            {cost.randuri.some((x) => x.derivat) && (
              <div className="sg-muted" style={{ fontSize: 11, marginTop: 4 }}>
                {t("struct.sumar.nota_derivat")}
              </div>
            )}
          </>
        )}
      </div>}

      {/* drumul spre pagina elementului stă pe ORICE piesă care are pagină —
          și când pașii de armare nu sunt încă hidratați (indexul e gol,
          verdictul de mai sus lipsește): pagina își cere singură pașii.
          Arhitectura și zidăria confinată n-au pagină: linkul ducea la
          «negăsit». */}
      {arePagina && (
        <div className="ob-studio-actiuni">
          <a className="ob-chip ob-c-acc" style={{ textDecoration: "none" }}
             href={hashElement(id, ref, solutie)} data-testid="sumar-pagina">
            {t("struct.sumar.pagina")}
          </a>
        </div>
      )}
    </>
  );
}
